// Receipt photo intake (Task 3, 2026-06-12).
//
// The admin photographs a supplier's paper ticket → Gemini Flash extracts the
// line items → a confirmation screen lets the admin review/edit every line,
// match each to an existing stock article OR create a new one (album/business
// flag) → ONLY on "Valider et ajouter au stock" does anything get written.
//
// TWO endpoints: /stock/receipt/extract (reads, writes nothing) and
// /stock/receipt/commit (the moving-average purchase, same ledger as a manual
// achat). Never auto-commits. Mobile-first — the admin uses a phone camera.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  stockApi,
  type Material,
  type ReceiptExtractLine,
  type ReceiptCommitLine,
} from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { useT } from '@/lib/i18n';

const GOLD_DARK = 'var(--color-adm-gold-ink)'; // #8B6F26 in light
const inputCls = 'w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white';

// A row in the confirmation table — the editable, reviewed shape of one line.
type ReviewRow = {
  id: string;               // stable React key (rows are deletable/addable)
  label: string;            // ticket text (read-only reference)
  qty: string;              // editable qty of purchase units
  unit_price: string;       // editable MAD / purchase unit
  // target resolution: '' = create new; otherwise an existing material id
  target: string;
  // for create-new
  new_name: string;
  new_class: 'album' | 'business';
};

function fromExtract(line: ReceiptExtractLine): ReviewRow {
  return {
    id: crypto.randomUUID(),
    label: line.label,
    qty: String(line.qty ?? 1),
    unit_price: line.unit_price != null ? String(line.unit_price) : '',
    target: line.matched && line.material_id ? line.material_id : '',
    new_name: line.matched ? line.label : (line.proposed?.name ?? line.label),
    new_class: line.matched
      ? (line.material_class ?? 'album')
      : (line.proposed?.material_class ?? 'album'),
  };
}

function Shell({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  const { t } = useT();
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: 'rgba(17,17,17,0.10)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-[680px] max-h-[90vh] overflow-y-auto bg-white rounded-2xl p-5 shadow-2xl adm-rise"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 mb-4">
          <h3 className="text-[16px] font-semibold" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>📷 {t('Scanner un ticket fournisseur')}</h3>
          <button
            type="button" onClick={onClose} aria-label={t('Fermer')}
            className="ml-auto w-8 h-8 rounded-lg border border-gray-200 text-gray-400 hover:text-gray-700 flex items-center justify-center"
          >✕</button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

// Downscale a camera photo client-side before upload. Phone photos are 3-12 MB
// and would blow the worker/OpenRouter request limit + 20s timeout. We load the
// File into an Image, scale so the longest edge ≤ 1600px (aspect kept), draw to
// a canvas and export JPEG @0.8 — a few hundred KB, plenty for OCR.
async function downscaleToDataUrl(file: File): Promise<string> {
  const MAX_EDGE = 1600;
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = () => reject(new Error("Impossible de lire l'image, réessayez."));
      im.src = objectUrl;
    });
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) throw new Error("Impossible de lire l'image, réessayez.");
    const scale = Math.min(1, MAX_EDGE / Math.max(w, h));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error("Impossible de lire l'image, réessayez.");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.8);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function ReceiptIntakeModal({ materials, onClose }: { materials: Material[]; onClose: () => void }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [stage, setStage] = useState<'pick' | 'review'>('pick');
  const [supplier, setSupplier] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [rows, setRows] = useState<ReviewRow[]>([]);

  const setRow = (id: string, patch: Partial<ReviewRow>) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  // ── Step 1: extract from photo (writes nothing) ──
  const extract = useMutation({
    mutationFn: async (file: File) => {
      const dataUrl = await downscaleToDataUrl(file);
      return stockApi.receiptExtract(dataUrl);
    },
    onSuccess: ({ receipt }) => {
      setSupplier(receipt.supplier ?? '');
      setPurchaseDate(receipt.purchase_date ?? '');
      setRows((receipt.lines ?? []).map(fromExtract));
      setStage('review');
    },
  });

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) extract.mutate(file);
  };

  // ── Step 2: commit the reviewed payload ──
  const commit = useMutation({
    mutationFn: () => {
      const lines: ReceiptCommitLine[] = rows.map((r) => {
        const qty = Number(r.qty);
        const price = Number(r.unit_price);
        const qtySafe = Number.isFinite(qty) ? qty : 0;
        const priceSafe = Number.isFinite(price) ? price : 0;
        if (r.target) {
          return { material_id: r.target, qty_purchase_units: qtySafe, unit_price: priceSafe };
        }
        return {
          new_material: {
            name: r.new_name.trim(),
            category: 'consommable',
            material_class: r.new_class,
          },
          qty_purchase_units: qtySafe,
          unit_price: priceSafe,
        };
      });
      return stockApi.receiptCommit({
        supplier: supplier || undefined,
        purchase_date: purchaseDate || undefined,
        note: 'Saisie ticket',
        lines,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-materials'] });
      queryClient.invalidateQueries({ queryKey: ['stock-purchases'] });
      queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
      queryClient.invalidateQueries({ queryKey: ['stock-alerts'] });
      onClose();
    },
  });

  // A row is valid: qty > 0, price ≥ 0, and a resolved target (existing id or a
  // non-empty new name). Empty rows can be removed but block commit if invalid.
  const rowValid = (r: ReviewRow) =>
    Number(r.qty) > 0 && Number(r.unit_price) >= 0 && (!!r.target || !!r.new_name.trim());
  const canCommit = rows.length > 0 && rows.every(rowValid);

  return (
    <Shell onClose={onClose}>
      {stage === 'pick' && (
        <div className="space-y-4">
          <p className="text-xs text-gray-500">
            {t('Prenez en photo le ticket / bon d’achat du fournisseur. L’IA lit les articles, puis')}{' '}
            <b>{t('vous validez chaque ligne')}</b>{' '}{t('avant d’ajouter au stock — rien n’est enregistré automatiquement.')}
          </p>
          <label
            className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed cursor-pointer py-10 px-4 text-center"
            style={{ borderColor: 'rgba(201,162,74,.4)', background: 'rgba(201,162,74,.05)' }}
          >
            <span className="text-3xl">📷</span>
            <span className="text-sm font-semibold" style={{ color: GOLD_DARK }}>
              {extract.isPending ? t('Lecture du ticket en cours…') : t('Prendre une photo / choisir une image')}
            </span>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              disabled={extract.isPending}
              onChange={onFile}
            />
          </label>
          {extract.isPending && <p className="text-xs text-center text-gray-400">{t('L’IA analyse l’image…')}</p>}
          {extract.isError && <p className="text-xs text-red-600 text-center">{(extract.error as Error).message}</p>}
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={onClose}>{t('Annuler')}</Button>
          </div>
        </div>
      )}

      {stage === 'review' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">{t('Fournisseur')}</label>
              <input className={inputCls} value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder={t('Nom du fournisseur')} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">{t('Date d’achat')}</label>
              <input className={inputCls} type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} />
            </div>
          </div>

          <p className="text-[11px] text-gray-400">
            {t('Vérifiez chaque ligne. Pour chaque article : rapprochez-le d’un article existant, ou créez-en un nouveau (album / business).')}
          </p>

          <div className="space-y-2">
            {rows.map((r) => {
              const valid = rowValid(r);
              return (
                <div
                  key={r.id}
                  className="rounded-xl border p-3 space-y-2"
                  style={{ borderColor: valid ? 'var(--color-adm-line)' : 'rgba(239,68,68,.4)' }}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] text-gray-400">{t('Ticket :')}</p>
                      <p className="text-sm font-semibold text-gray-800 truncate" title={r.label}>{r.label || t('(vide)')}</p>
                    </div>
                    <button
                      type="button"
                      className="text-red-300 hover:text-red-500 text-sm px-1"
                      title={t('Retirer cette ligne')}
                      onClick={() => setRows((rs) => rs.filter((x) => x.id !== r.id))}
                    >✕</button>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-500 mb-0.5">{t('Quantité')}</label>
                      <input className={inputCls} type="number" min="0.01" step="0.01" value={r.qty} onChange={(e) => setRow(r.id, { qty: e.target.value })} />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-500 mb-0.5">{t('Prix unitaire (MAD)')}</label>
                      <input className={inputCls} type="number" min="0" step="0.01" value={r.unit_price} onChange={(e) => setRow(r.id, { unit_price: e.target.value })} placeholder="0" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 mb-0.5">{t('Article de stock')}</label>
                    <select
                      className={inputCls}
                      value={r.target}
                      onChange={(e) => setRow(r.id, { target: e.target.value })}
                    >
                      <option value="">➕ {t('Créer un nouvel article')}</option>
                      {materials.map((m) => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                  </div>

                  {!r.target && (
                    <div className="grid grid-cols-2 gap-2 rounded-lg p-2" style={{ background: 'rgba(201,162,74,.06)' }}>
                      <div>
                        <label className="block text-[11px] font-semibold text-gray-500 mb-0.5">{t('Nom du nouvel article')}</label>
                        <input className={inputCls} value={r.new_name} onChange={(e) => setRow(r.id, { new_name: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-gray-500 mb-0.5">{t('Classe')}</label>
                        <select
                          className={inputCls}
                          value={r.new_class}
                          onChange={(e) => setRow(r.id, { new_class: e.target.value as 'album' | 'business' })}
                        >
                          <option value="album">{t('Album (entre dans le coût)')}</option>
                          <option value="business">{t('Business (suivi seul)')}</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {rows.length === 0 && (
              <p className="text-xs text-gray-400 py-4 text-center">{t('Aucune ligne détectée — réessayez avec une photo plus nette.')}</p>
            )}
          </div>

          <button
            type="button"
            onClick={() => setRows((rs) => [...rs, { id: crypto.randomUUID(), label: '', qty: '1', unit_price: '', target: '', new_name: '', new_class: 'album' }])}
            className="text-xs font-semibold" style={{ color: GOLD_DARK }}
          >
            + {t('Ajouter une ligne manuellement')}
          </button>

          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <button type="button" className="text-xs text-gray-500 underline" onClick={() => setStage('pick')}>← {t('Reprendre une photo')}</button>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={onClose}>{t('Annuler')}</Button>
              <Button size="sm" onClick={() => commit.mutate()} loading={commit.isPending} disabled={!canCommit}>
                {t('Valider et ajouter au stock')}
              </Button>
            </div>
          </div>
          {commit.isError && <p className="text-xs text-red-600">{(commit.error as Error).message}</p>}
        </div>
      )}
    </Shell>
  );
}

export default ReceiptIntakeModal;
